import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "types/store";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";
import moment from "moment";
import * as TenantAPI from "api/TenantAPI";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import DatePicker from "react-datepicker";
import AgentC from "../Templates/AgentC";
import styles from "./style.module.css";
import { ButtonGroup, Button } from "aible";
import SearchInput from "../Inputs/SearchInput/SearchInput";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator";
import * as viewReducer from "@/containers/View/reducer";
import * as blueprintReducer from "@/containers/Blueprint/reducer";
import * as foldersReducer from "@/containers/Folders/reducer";
import * as accountsReducer from "@/containers/Accounts/reducer";
import * as adminReducer from "@/containers/Admin/reducer";
var FilterTypes;
(function (FilterTypes) {
    FilterTypes["ALL_AGENTS"] = "ALL_AGENTS";
    FilterTypes["ALL_USERS"] = "ALL_USERS";
})(FilterTypes || (FilterTypes = {}));
var TopTypes;
(function (TopTypes) {
    TopTypes["AGENT_USAGE"] = "AGENT_USAGE";
    TopTypes["USER_ACTIVITY"] = "USER_ACTIVITY";
})(TopTypes || (TopTypes = {}));
var DATE_GRANULARITY;
(function (DATE_GRANULARITY) {
    DATE_GRANULARITY["DAILY"] = "DAILY";
    DATE_GRANULARITY["WEEKLY"] = "WEEKLY";
})(DATE_GRANULARITY || (DATE_GRANULARITY = {}));
export const colorsTwo = [
    "#D57DF5",
    "#7E69D7",
    "#2F80ED",
    "#56CCF2",
    "#52FFD3",
    "#5BCF74",
    "#96E964",
    "#FFCF71",
    "#F97255",
    "#FF84CE"
];
const downloadCSV = (data) => {
    const blob = new Blob([convertToCSV(data)], {
        type: "text/csv;charset=utf-8;"
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "export.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
function convertToCSV(data) {
    let csvRows = [];
    // Loop over the rows
    data.forEach((row) => {
        csvRows.push(row.join(","));
    });
    return csvRows.join("\n");
}
function getMonitorDataDisplayName(monitorData) {
    const DEFAULT_NAME = "No Agent";
    if (!monitorData) {
        return DEFAULT_NAME;
    }
    if (monitorData.name) {
        return monitorData.name;
    }
    if (monitorData.id) {
        if (Number(monitorData.id) >= 0) {
            return String(monitorData.id);
        }
    }
    return DEFAULT_NAME;
}
const getDateFromTimestamp = (dateString, dateGranularity) => {
    if (dateGranularity === DATE_GRANULARITY.DAILY) {
        return dateString;
    }
    if (dateGranularity === DATE_GRANULARITY.WEEKLY) {
        const rawDate = new Date(dateString);
        const weekdayDiff = rawDate.getDay() + 1; // assuming weeks start on sunday
        rawDate.setDate(rawDate.getDate() - weekdayDiff);
        const weekStart = rawDate.toISOString().split("T")[0];
        return weekStart;
    }
    return dateString;
};
const AgentMonitor = ({}) => {
    const FILTER_FOLDER = true;
    const dispatch = useDispatch();
    const [searchInput, setSearchInput] = useState(undefined);
    const [filterType, setFilterType] = useState(FilterTypes.ALL_AGENTS);
    const [topType, setTopTypes] = useState(TopTypes.AGENT_USAGE);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [dateGranularity, setDateGranularity] = useState(DATE_GRANULARITY.WEEKLY);
    const [agentData, setAgentData] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const blueprints = useSelector((state) => blueprintReducer.getBlueprints(state));
    const tenantId = useSelector((state) => accountsReducer.getSelectedAccount(state)?.tenant_id);
    const [filterNoAgent, setFilterNoAgent] = useState(false);
    const folderId = useSelector((state) => foldersReducer.getSelectedFolder(state));
    const allUsers = useSelector((state) => adminReducer.getUsers(state));
    const sideAgent = useSelector((state) => viewReducer.getSideAgent(state));
    const isShowSideAgent = useSelector((state) => viewReducer.getIsShowSideAgent(state));
    const [isShowAgentTable, setIsShowAgentTable] = useState(false);
    const retAgentData = agentData?.report?.agents?.usage
        ?.filter((d) => (fromDate === null || moment(d?.[0]).isAfter(moment(fromDate))) &&
        (toDate === null || moment(d?.[0]).isBefore(moment(toDate))))
        .filter((d) => FILTER_FOLDER !== true || d?.[3] === folderId)
        .filter((d) => filterNoAgent !== true || d?.[1] !== -1);
    let retMonitorData = {};
    retAgentData?.forEach((agentRow, i) => {
        const agentId = agentRow?.[1];
        const queries = agentRow?.[6];
        if (retMonitorData?.[agentId] === undefined) {
            const matchingBlueprint = (blueprints ?? [])?.find((b) => b?.id === agentId);
            retMonitorData[agentId] = {
                id: agentId,
                queries: queries,
                blueprint: matchingBlueprint,
                name: matchingBlueprint?.name,
                userCount: (agentRow?.[16] ?? []).length,
                sessionCount: (agentRow?.[16] ?? []).reduce((a, b) => a + b?.[1], 0)
            };
        }
        else if (retMonitorData?.[agentId]?.queries !== undefined) {
            retMonitorData[agentId].queries += queries;
            retMonitorData[agentId].userCount += (agentRow?.[16] ?? []).length;
            retMonitorData[agentId].sessionCount += (agentRow?.[16] ?? []).reduce((a, b) => a + b?.[1], 0);
        }
    });
    let dayCounts = {};
    retAgentData?.forEach((agentRow, i) => {
        const isoDate = agentRow?.[0];
        const rawDate = isoDate.slice(0, 10); // "YYYY-MM-DD"
        const day = getDateFromTimestamp(rawDate, dateGranularity);
        const agentId = agentRow?.[1];
        const queries = agentRow?.[6];
        if (dayCounts[day] === undefined) {
            dayCounts[day] = {};
        }
        if (dayCounts?.[day]?.[agentId] === undefined) {
            dayCounts[day][agentId] = queries;
        }
        else {
            dayCounts[day][agentId] += queries;
        }
    });
    let dayCountsUsers = {};
    retAgentData?.forEach((agentRow, i) => {
        const isoDate = agentRow?.[0];
        const rawDate = isoDate.slice(0, 10); // "YYYY-MM-DD"
        const day = getDateFromTimestamp(rawDate, dateGranularity);
        const userRows = agentRow?.[16];
        if (dayCountsUsers[day] === undefined) {
            dayCountsUsers[day] = {};
        }
        userRows.forEach((userRow) => {
            const userName = userRow?.[0]?.[1];
            if (dayCountsUsers?.[day]?.[userName] === undefined) {
                dayCountsUsers[day][userName] = userRow?.[2];
            }
            else {
                dayCountsUsers[day][userName] += userRow?.[2];
            }
        });
    });
    let userData = {};
    retAgentData?.forEach((agentRow) => {
        const userRows = agentRow?.[16];
        userRows.map((userRow) => {
            const userName = userRow?.[0]?.[1];
            if (userData[userName] === undefined) {
                userData[userName] = {
                    sessions: userRow?.[1],
                    interactions: userRow?.[2],
                    id: userRow?.[0]?.[0]
                };
            }
            else {
                userData[userName].sessions += userRow?.[1];
                userData[userName].interactions += userRow?.[2];
            }
        });
    });
    const totalQueries = Object.keys(retMonitorData)?.reduce((a, b) => a + retMonitorData[b]?.queries, 0);
    let chartData = [];
    Object.keys(dayCounts).map((day) => {
        const dayBucket = dayCounts[day];
        let chartRow = { date: day };
        Object.keys(dayBucket).map((agentId) => {
            const matchingBlueprint = (blueprints ?? [])?.find((b) => String(b?.id) === agentId);
            const aName = getMonitorDataDisplayName({
                name: matchingBlueprint?.name,
                id: agentId
            });
            chartRow[aName] = dayBucket[agentId];
        });
        chartData.push(chartRow);
    });
    let chartDataUsers = [];
    Object.keys(dayCountsUsers).map((day) => {
        const dayBucket = dayCountsUsers[day];
        let chartRow = { date: day };
        Object.keys(dayBucket).map((userName) => {
            chartRow[userName] = dayBucket[userName];
        });
        chartDataUsers.push(chartRow);
    });
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            const defaultFromDate = new Date();
            defaultFromDate.setMonth(defaultFromDate.getMonth() - 3);
            setFromDate(defaultFromDate);
            const usageReport = await TenantAPI.getUsageReport(tenantId);
            setAgentData(usageReport);
            setIsLoading(false);
        };
        fetchData();
    }, []);
    return isLoading ? (React.createElement("div", { className: styles.agentMonitorLoading },
        React.createElement(LoadingIndicator, { style: { margin: "0px", marginBottom: "40px" } }),
        "Fetching Data...")) : (React.createElement("div", { style: { position: "relative" } },
        sideAgent !== undefined && isShowSideAgent && (React.createElement("div", { className: styles.agentRightPanel },
            React.createElement(AgentC, { agent: sideAgent }))),
        sideAgent !== undefined && (React.createElement("div", { className: styles.sideAgentTableButton },
            React.createElement(Button, { onClick: () => setIsShowAgentTable(!isShowAgentTable), active: isShowAgentTable }, "Activity Log"))),
        sideAgent !== undefined && (React.createElement("div", { className: styles.sideAgentButton },
            React.createElement(Button, { onClick: () => dispatch(viewReducer.updateIsShowSideAgent(!isShowSideAgent)) }, isShowSideAgent ? "Hide Settings" : "Show Settings"))),
        React.createElement("div", { className: styles.monitoringButton },
            React.createElement(Button, { onClick: () => downloadCSV([agentData?.report?.agents?.usage?.[0], ...(retAgentData ?? [])]) }, "Download CSV")),
        isShowAgentTable ? (React.createElement(ActivityTable, null)) : (React.createElement("div", null,
            React.createElement("div", { className: styles.agentMonitorTop },
                React.createElement("div", { className: styles.agentMonitorBox, style: { minWidth: "calc(60% - 20px)", marginLeft: "10px" } },
                    React.createElement("div", { className: styles.agentMonitorBoxHeader, style: { justifyContent: "flex-start" } },
                        "Total",
                        React.createElement(ButtonGroup, { style: { marginLeft: "10px", marginRight: "10px" } },
                            React.createElement(Button, { onClick: () => setTopTypes(TopTypes.AGENT_USAGE), active: topType === TopTypes.AGENT_USAGE }, "Agent Usage"),
                            React.createElement(Button, { onClick: () => setTopTypes(TopTypes.USER_ACTIVITY), active: topType === TopTypes.USER_ACTIVITY }, "User Activity")),
                        React.createElement("div", { style: { marginRight: "10px" } }, "from"),
                        React.createElement(DatePicker, { selected: fromDate, onChange: (x) => {
                                setFromDate(x);
                            }, placeholderText: "Anytime", className: styles.datePickerInput }),
                        React.createElement("div", { style: { marginLeft: "10px", marginRight: "10px" } }, "to"),
                        React.createElement(DatePicker, { selected: toDate, onChange: (x) => {
                                setToDate(x);
                            }, placeholderText: "Today", className: styles.datePickerInput }),
                        React.createElement("div", { style: { marginLeft: "10px", marginRight: "10px" } }, "grouped"),
                        React.createElement(ButtonGroup, null,
                            React.createElement(Button, { onClick: () => setDateGranularity(DATE_GRANULARITY.WEEKLY), active: dateGranularity === DATE_GRANULARITY.WEEKLY }, "Weekly"),
                            React.createElement(Button, { onClick: () => setDateGranularity(DATE_GRANULARITY.DAILY), active: dateGranularity === DATE_GRANULARITY.DAILY }, "Daily")),
                        React.createElement("div", { className: styles.agentMonitorBoxHeaderRight },
                            React.createElement("div", { className: styles.agentMonitorBig }, totalQueries),
                            React.createElement("div", { className: styles.agentMonitorSmall }, "Total Queries"))),
                    topType === TopTypes.USER_ACTIVITY ? (React.createElement(ResponsiveContainer, { width: "100%", height: "100%" },
                        React.createElement(BarChart, { style: { width: "100%", height: "100%" }, data: chartDataUsers, margin: {
                                top: 20,
                                right: 0,
                                left: 0,
                                bottom: 5
                            } },
                            React.createElement(CartesianGrid, { strokeDasharray: "3 3" }),
                            React.createElement(XAxis, { dataKey: "date" }),
                            React.createElement(YAxis, null),
                            React.createElement(Tooltip, null),
                            React.createElement(Legend, null),
                            Object.keys(userData)
                                .reverse()
                                .map((userName, i) => (React.createElement(Bar, { dataKey: userName, stackId: "a", fill: colorsTwo[i % colorsTwo.length] })))))) : (React.createElement("div", { className: styles.agentMonitorBoxContent, style: { marginTop: "10px", position: "relative" } },
                        React.createElement(ResponsiveContainer, { width: "100%", height: "100%" },
                            React.createElement(BarChart, { style: { width: "100%", height: "100%" }, data: chartData, margin: {
                                    top: 20,
                                    right: 0,
                                    left: 0,
                                    bottom: 5
                                } },
                                React.createElement(CartesianGrid, { strokeDasharray: "3 3" }),
                                React.createElement(XAxis, { dataKey: "date" }),
                                React.createElement(YAxis, null),
                                React.createElement(Tooltip, null),
                                React.createElement(Legend, null),
                                Object.keys(retMonitorData)
                                    .reverse()
                                    .map((agentId, i) => (React.createElement(Bar, { dataKey: getMonitorDataDisplayName({
                                        name: retMonitorData[agentId]?.blueprint?.name,
                                        id: agentId
                                    }), stackId: "a", fill: colorsTwo[i % colorsTwo.length] }))))),
                        React.createElement("div", { className: styles.agentMonitorAbsAction },
                            React.createElement(Button, { onClick: () => setFilterNoAgent(!filterNoAgent), style: { padding: "5px" } }, filterNoAgent === true ? "Show All" : "Hide Standard"))))),
                React.createElement("div", { className: styles.agentMonitorBox, style: { minWidth: "calc(20% - 10px)", overflow: "auto" } },
                    React.createElement("div", { className: styles.agentMonitorBoxHeader },
                        React.createElement("div", null, "Agents Used"),
                        React.createElement("div", null, Math.max(Object.keys(retMonitorData)?.length - 1, 0))),
                    React.createElement("div", { className: styles.agentMonitorTable },
                        React.createElement("div", { className: styles.agentMonitorTableHeader },
                            React.createElement("div", null, "Name"),
                            React.createElement("div", null, "Queries")),
                        Object.keys(retMonitorData).map((a) => (React.createElement("div", { className: styles.agentMonitorTableRow },
                            React.createElement("div", null, getMonitorDataDisplayName(retMonitorData[a])),
                            React.createElement("div", null, retMonitorData[a]?.queries)))))),
                React.createElement("div", { className: styles.agentMonitorBox, style: { minWidth: "calc(20% - 10px)", marginRight: "10px", overflow: "auto" } },
                    React.createElement("div", { className: styles.agentMonitorBoxHeader },
                        React.createElement("div", null, "Active Users"),
                        React.createElement("div", null, Object.keys(userData ?? {}).length)),
                    React.createElement("div", { className: styles.agentMonitorTable },
                        React.createElement("div", { className: styles.agentMonitorTableHeader },
                            React.createElement("div", null, "Name"),
                            React.createElement("div", null, "Queries")),
                        Object.keys(userData)?.map((user) => (React.createElement("div", { className: styles.agentMonitorTableRow },
                            React.createElement("div", null, user),
                            React.createElement("div", null, userData[user]?.interactions))))))),
            React.createElement("div", { className: styles.agentMonitorBot },
                React.createElement("div", { className: styles.agentMonitorBox, style: {
                        width: "calc(100% - 20px)",
                        marginLeft: "10px",
                        marginRight: "10px",
                        marginTop: "20px"
                    } },
                    React.createElement("div", { className: styles.agentMonitorBotHeader },
                        React.createElement(ButtonGroup, { style: { marginRight: "20px" } },
                            React.createElement(Button, { onClick: () => setFilterType(FilterTypes.ALL_AGENTS), active: filterType === FilterTypes.ALL_AGENTS }, "All Agents"),
                            React.createElement(Button, { onClick: () => setFilterType(FilterTypes.ALL_USERS), active: filterType === FilterTypes.ALL_USERS }, "All Users")),
                        React.createElement(SearchInput, { value: searchInput, onChange: (val) => setSearchInput(val) })),
                    filterType === FilterTypes.ALL_USERS ? (React.createElement("div", { className: styles.agentMonitorTable, style: { marginTop: "10px", maxHeight: "21vh", overflow: "auto" } },
                        React.createElement("div", { className: styles.agentMonitorTableHeader },
                            React.createElement("div", { className: styles.agentMonitorTableCell }, "Name"),
                            React.createElement("div", { className: styles.agentMonitorTableCellR }, "Queries"),
                            React.createElement("div", { className: styles.agentMonitorTableCellR }, "Sessions"),
                            React.createElement("div", { className: styles.agentMonitorTableCellR }, "User Creation Date")),
                        Object.keys(userData)
                            ?.filter((a) => searchInput === undefined || (a ?? "").toLowerCase().indexOf(searchInput.toLowerCase()) !== -1)
                            .map((a) => (React.createElement("div", { className: styles.agentMonitorTableRow },
                            React.createElement("div", { className: styles.agentMonitorTableCell }, a),
                            React.createElement("div", { className: styles.agentMonitorTableCellR }, userData[a]?.interactions),
                            React.createElement("div", { className: styles.agentMonitorTableCellR }, userData[a]?.sessions),
                            React.createElement("div", { className: styles.agentMonitorTableCellR }, allUsers?.find((au) => au?.id === userData[a]?.id)?.created_date !== undefined
                                ? moment(allUsers?.find((au) => au?.id === userData[a]?.id)?.created_date).format("MM-DD-YYYY")
                                : "")))))) : (React.createElement("div", { className: styles.agentMonitorTable, style: { marginTop: "10px", maxHeight: "21vh", overflow: "auto" } },
                        React.createElement("div", { className: styles.agentMonitorTableHeader },
                            React.createElement("div", { className: styles.agentMonitorTableCell }, "Name"),
                            React.createElement("div", { className: styles.agentMonitorTableCellR }, "Queries"),
                            React.createElement("div", { className: styles.agentMonitorTableCellR }, "Total Users"),
                            React.createElement("div", { className: styles.agentMonitorTableCellR }, "Total Sessions"),
                            React.createElement("div", { className: styles.agentMonitorTableCellR }, "Agent Creation Date")),
                        Object.keys(retMonitorData)
                            ?.filter((a) => searchInput === undefined ||
                            getMonitorDataDisplayName(retMonitorData[a])
                                .toLowerCase()
                                .indexOf(searchInput.toLowerCase()) !== -1)
                            .map((a) => (React.createElement("div", { className: styles.agentMonitorTableRow },
                            React.createElement("div", { className: styles.agentMonitorTableCell }, getMonitorDataDisplayName(retMonitorData[a])),
                            React.createElement("div", { className: styles.agentMonitorTableCellR }, retMonitorData[a]?.queries),
                            React.createElement("div", { className: styles.agentMonitorTableCellR }, retMonitorData[a]?.userCount),
                            React.createElement("div", { className: styles.agentMonitorTableCellR }, retMonitorData[a]?.sessionCount),
                            React.createElement("div", { className: styles.agentMonitorTableCellR }, retMonitorData[a]?.blueprint?.created_date !== undefined
                                ? moment(retMonitorData[a]?.blueprint?.created_date).format("MM-DD-YYYY")
                                : ""))))))))))));
};
const fakeData = [
    {
        task: "Data Sync",
        time: "11:00am PST",
        activity: "Pulled latest CRM records",
        result: "Success"
    },
    {
        task: "Email Scan",
        time: "11:05am PST",
        activity: "Scanned inbox for support tickets",
        result: "3 Issues Found"
    },
    {
        task: "Report Generation",
        time: "11:06am PST",
        activity: "Compiled weekly analytics report",
        result: "Completed"
    },
    {
        task: "API Health Check",
        time: "11:10am PST",
        activity: "Pinged external services",
        result: "All Systems Operational"
    }
];
const ActivityTable = () => {
    return (React.createElement("div", { className: styles.card },
        React.createElement("div", { className: styles.headerRow },
            React.createElement("div", { className: styles.task, style: { fontWeight: "bold", color: "#1a3f7a" } }, "Task"),
            React.createElement("div", { className: styles.time, style: { fontWeight: "bold", color: "#1a3f7a" } }, "Time"),
            React.createElement("div", { className: styles.activity, style: { fontWeight: "bold", color: "#1a3f7a" } }, "Activity"),
            React.createElement("div", { className: styles.result, style: { fontWeight: "bold", color: "#1a3f7a" } }, "Result")),
        fakeData.map((row, index) => (React.createElement("div", { key: index, className: styles.dataRow },
            React.createElement("div", { className: styles.task }, row.task),
            React.createElement("div", { className: styles.time }, row.time),
            React.createElement("div", { className: styles.activity }, row.activity),
            React.createElement("div", { className: styles.result }, row.result))))));
};
export default AgentMonitor;
